import { useAuth0 } from '@auth0/auth0-react'
import { getLaunchMode } from '@/features/auth/launchContext'
import { useEpicSessionExpiry } from '@/features/auth/useEpicSessionExpiry'
import EpicSessionLockout from '@/features/auth/components/EpicSessionLockout'
import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'

interface SecureEHRRouteProps {
  children: React.ReactNode
}

/**
 * Route guard that supports both Epic SMART on FHIR and standalone Auth0 flows.
 *
 * Grants access when ANY of the following is true:
 *  1. Auth0 `isAuthenticated` — standalone web flow.
 *  2. `location.state` carries an `epicState` value (string or object) — in-memory
 *     navigation from the callback page.
 *  3. `getLaunchMode() === 'epic_smart'` — the active Epic OAuth state is persisted
 *     in sessionStorage AND the corresponding session exists in EpicAuthClient.
 *     This covers hard-refreshes and direct deep links to /order/* routes.
 */
const SecureEHRRoute: React.FC<SecureEHRRouteProps> = ({ children }) => {
  const location = useLocation()
  const { isAuthenticated, isLoading } = useAuth0()
  const { isExpired: isEpicSessionExpired } = useEpicSessionExpiry()

  // Epic state from router navigation state (forward nav from callback page).
  const locationState = location.state as { epicState?: string } | string | undefined
  const epicStateFromNav =
    typeof locationState === 'object' && locationState !== null
      ? locationState.epicState
      : typeof locationState === 'string'
        ? locationState
        : undefined

  // Epic auth from persisted sessionStorage (covers page reload + deep links).
  const hasEpicAuth = Boolean(epicStateFromNav) || getLaunchMode() === 'epic_smart'

  if (isLoading && !hasEpicAuth) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-secondary-medium dark:bg-gray-800">
        <div className="w-full max-w-md p-8">
          <div className="rounded-base border border-border-base bg-white p-8 shadow-lg dark:border-gray-700 dark:bg-gray-900">
            <div className="mt-8">
              <div className="text-center">
                <div
                  className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand-600 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite] dark:border-brand-500"
                  role="status"
                >
                  <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                    Loading...
                  </span>
                </div>
                <p className="mt-2 text-sm text-text-body dark:text-gray-400">Authenticating...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (hasEpicAuth || isAuthenticated) {
    // Epic access tokens expire ~1h after launch and cannot be refreshed in-app.
    // Once expired (with buffer), block the workspace and instruct the clinician
    // to relaunch from Epic rather than let downstream Epic/FHIR calls fail.
    // `isEpicSessionExpired` is only ever true for an active Epic launch, so
    // standalone Auth0 sessions are unaffected.
    if (isEpicSessionExpired) {
      return <EpicSessionLockout />
    }
    return <>{children}</>
  }

  return <Navigate to="/login" state={{ from: location }} replace />
}

export default SecureEHRRoute
